import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { hash, stableStringify } from "@hypertune/sdk/src/shared";
import {
  AnalyticsViewConfig,
  analyticsViewConfigFunnelType,
} from "@hypertune/shared-internal/src/types";

export const countVariants = ["Unique", "Total"] as const;
export type CountsVariant = (typeof countVariants)[number];

export const hypothesisVariants = ["One-sided", "Two-sided"] as const;
export type HypothesisVariant = (typeof hypothesisVariants)[number];

export type AnalyticsEditorState = {
  /** The hash of the config in the saved state of the view. */
  activeConfigHash: string;
  /** The hash of the config in the current working state of the view. */
  configHash: string;
  config: AnalyticsViewConfig;
  hasChanges: boolean;
  showNewViewModal: boolean;
};

const initialState: AnalyticsEditorState = {
  config: {
    type: analyticsViewConfigFunnelType,
    steps: [],
  },
  activeConfigHash: "",
  configHash: "",
  hasChanges: false,
  showNewViewModal: false,
};

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    setActiveAnalyticsView: (
      draftState,
      action: PayloadAction<AnalyticsViewConfig>
    ) => {
      draftState.activeConfigHash = getConfigHash(action.payload);
      updateConfig(draftState, action.payload);
    },
    resetAnalyticsEditor: (draftState) => {
      Object.assign(draftState, initialState);
    },
    setConfig: (draftState, action: PayloadAction<AnalyticsViewConfig>) =>
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updateConfig(draftState, action.payload),
    setShowNewViewModal: (draftState, action: PayloadAction<boolean>) => {
      draftState.showNewViewModal = action.payload;
    },
  },
});

export const {
  setActiveAnalyticsView,
  resetAnalyticsEditor,
  setConfig,
  setShowNewViewModal,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;

function updateConfig(
  draftState: AnalyticsEditorState,
  newConfig: AnalyticsViewConfig
): void {
  const newConfigHash = getConfigHash(newConfig);

  if (newConfigHash !== draftState.configHash) {
    console.log("Updating analytics view config", { newConfig });
    draftState.config = newConfig;
    draftState.configHash = newConfigHash;
  }
  draftState.hasChanges = draftState.activeConfigHash !== newConfigHash;
}

function getConfigHash(config: AnalyticsViewConfig): string {
  return hash(stableStringify(config)).toString();
}
